import { memo, useEffect, useRef, useState } from 'react'

import i18n from 'i18n-literally'
import PropTypes from 'prop-types'
import { trim } from 'ramda'
import { useConnect } from 'redux-bundler-hook'

import { MailOutlined as MailOutlinedIcon } from '@mui/icons-material'
import { Box, Grid, InputAdornment, Typography } from '@mui/material'

import { errorMessagePropType } from '~/src/Lib/PropTypes'
import { isValidEmail } from '~/src/Store/bundles/me/shape'
import Button from '~/src/UI/Shared/Button'
import FormikField from '~/src/UI/Shared/Form/FormikField'
import Icon from '~/src/UI/Shared/Icon'
import PageTitle from '~/src/UI/Shared/PageTitle'

import { PUBLIC_ROUTES } from '../constants'

export const ForgotComponent = props => {
  const {
    auth: {
      forgot: { loading, error },
    },
    doForgotClearState,
    doForgotPassword,
    doUpdateUrl,
    tokenValidationResponse,
  } = props

  const [formState, setFormState] = useState({
    email: '',
    expiredEmail: '',
  })
  const didInitializeRef = useRef(false)

  const { loading: tokenLoading, response } = tokenValidationResponse
  const { valid, email } = response

  useEffect(() => {
    if (didInitializeRef.current) return
    didInitializeRef.current = true

    doForgotClearState()

    if (!tokenLoading && !valid && email) {
      setFormState({
        email,
        expiredEmail: email,
      })
    }
  }, [doForgotClearState, email, tokenLoading, valid])

  const isFormValid = () => {
    const trimmedEmail = trim(formState.email)
    return Boolean(trimmedEmail && isValidEmail(trimmedEmail))
  }

  const onChange = nextState => {
    setFormState(prev => ({
      ...prev,
      ...nextState,
    }))
  }

  const onSubmit = async event => {
    event.preventDefault()
    if (isFormValid()) {
      await doForgotPassword(formState)
      doUpdateUrl(PUBLIC_ROUTES.LOGIN)
    }
  }

  return (
    <Grid
      aria-label={i18n`Forgot password`}
      data-testid="password-forgot-route"
      container
      component="section"
      spacing={0}
    >
      {formState.expiredEmail || error ? (
        <div className="aroya-login-banner">
          <Typography
            className="alert alert-danger"
            sx={{
              lineHeight: { xs: 1.2, sm: 1.75 },
              fontSize: { xs: 14, sm: 16 },
            }}
            variant="subtitle1"
          >
            {formState.expiredEmail
              ? i18n`Your email link has expired, click resend email to generate a new link.`
              : null}
            {error || null}
          </Typography>
        </div>
      ) : null}
      <Grid sx={{ paddingBottom: theme => theme.spacing(3) }} size={12}>
        <PageTitle disablePortal title={i18n`Reset your password`} align="center" />
      </Grid>
      <Grid size={12}>
        <Box
          component="form"
          onSubmit={onSubmit}
          sx={{
            mx: 2,
            px: { xs: 0, sm: 'calc(50% - 250px)' },
          }}
        >
          <Box
            component="fieldset"
            sx={{
              border: 'none',
            }}
          >
            <FormikField
              required={i18n`Please enter an email address`}
              type="email"
              margin="normal"
              id="email"
              name="email"
              label={i18n`Email Address`}
              value={formState.email}
              onChange={onChange}
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <Icon icon={MailOutlinedIcon} />
                  </InputAdornment>
                ),
              }}
            />

            <Box
              sx={{
                mt: 3,
                textAlign: 'center',
              }}
            >
              <Button type="submit" disabled={loading || !isFormValid()}>
                {formState.expiredEmail ? i18n`Resend email` : i18n`Reset your password`}
              </Button>
            </Box>
            {formState.expiredEmail ? null : (
              <Box
                sx={{
                  mt: 2.5,
                  textAlign: 'center',
                  color: 'text.primary',
                }}
              >
                <Button color="inherit" variant="text" href={PUBLIC_ROUTES.LOGIN}>
                  {i18n`Already have an account?`}
                </Button>
              </Box>
            )}
          </Box>
        </Box>
      </Grid>
    </Grid>
  )
}

ForgotComponent.propTypes = {
  doUpdateUrl: PropTypes.func.isRequired,
  doForgotPassword: PropTypes.func.isRequired,
  doForgotClearState: PropTypes.func.isRequired,
  tokenValidationResponse: PropTypes.shape({
    loading: PropTypes.bool,
    response: PropTypes.shape({
      valid: PropTypes.bool,
      email: PropTypes.string,
    }),
  }).isRequired,
  auth: PropTypes.shape({
    forgot: PropTypes.shape({
      error: errorMessagePropType,
      loading: PropTypes.bool,
    }),
  }).isRequired,
}

const MemoizedForgotComponent = memo(ForgotComponent)

const Connected = () => {
  const connectedProps = useConnect(
    'doUpdateUrl',
    'doForgotPassword',
    'doForgotClearState',
    'selectTokenValidationResponse',
    'selectAuth',
  )
  return <MemoizedForgotComponent {...connectedProps} />
}

export default Connected
